import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { websiteInterviewAPI } from '../api/websiteInterviewAPI';
import { useProductBackHandler } from '../../../components/ProductBackButton/ProductBackProvider';
import { useScrollDocumentToTop } from '../../../lib/scrollDocumentToTop';
import { sanitizeHtml } from '../../../lib/sanitizeHtml';
import './WebsiteInterviewPage.css';

const ROLE_OPTIONS = [
  { value: 'Investment Banking', label: 'Investment Banking' },
  { value: 'Private Equity', label: 'Private Equity' },
  { value: 'Engineering', label: 'Engineering' },
  { value: 'Computer Science', label: 'Computer Science' },
  { value: 'Law', label: 'Law' },
  { value: 'Operations (Financial Services)', label: 'Operations (Financial Services)' },
  { value: 'Medicine MMI', label: 'Medicine MMI (Multiple Mini Interview)' },
  { value: 'Medicine Panel', label: 'Medicine Panel Interview' },
  { value: 'Oxbridge', label: 'Oxbridge (Oxford/Cambridge)' },
  { value: 'Scholarship', label: 'Scholarship' },
];

const TOOL_TYPE_CONFIG = {
  // Specialist medicine / Oxbridge tools
  medicine_mmi_interview: {
    role: 'Medicine MMI',
    label: 'MMI Interview (Medicine)',
  },
  medicine_panel_interview: {
    role: 'Medicine Panel',
    label: 'Panel Interview (Medicine)',
  },
  oxbridge_interview: {
    role: 'Oxbridge',
    label: 'Oxbridge Interview',
  },
  interview_simulator: {
    role: '',
    label: 'Interview Simulator',
  },

  // New: role-specific interview tools (same engine, fixed role)
  investment_banking_interview: {
    role: 'Investment Banking',
    label: 'Investment Banking Interview',
  },
  private_equity_interview: {
    role: 'Private Equity',
    label: 'Private Equity Interview',
  },
  engineering_interview: {
    role: 'Engineering',
    label: 'Engineering Interview',
  },
  computer_science_interview: {
    role: 'Computer Science',
    label: 'Computer Science Interview',
  },
  law_interview: {
    role: 'Law',
    label: 'Law Interview',
  },
  operations_fs_interview: {
    role: 'Operations (Financial Services)',
    label: 'Operations Interview (Financial Services)',
  },
  scholarship_interview: {
    role: 'Scholarship',
    label: 'Scholarship Interview',
  },
};

const WebsiteInterviewPage = ({ caseStudyContext, appContext, onComplete, initialRole, toolType }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const userName = user?.name || 'Student';
  const userRole = user?.role || 'Student';
  const userEmail = user?.email || 'student@pathbuddy.com';

  // State management
  // If caseStudyContext provided (embedded mode), skip setup and go straight to interview
  const [currentView, setCurrentView] = useState(caseStudyContext ? 'conversation' : 'setup'); // 'setup', 'conversation', 'feedback'
  // Setup, the live interview, and the report replace each other in place.
  useScrollDocumentToTop(currentView);
  const [cvFile, setCvFile] = useState(null);
  const [role, setRole] = useState(initialRole || '');
  const [experienceLevel, setExperienceLevel] = useState('');
  const [jobUrl, setJobUrl] = useState('');
  const [jobInfo, setJobInfo] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isProcessingResponse, setIsProcessingResponse] = useState(false);
  
  // Keep refs in sync with state
  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);
  
  useEffect(() => {
    isProcessingResponseRef.current = isProcessingResponse;
  }, [isProcessingResponse]);
  
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(caseStudyContext ? 3 : 8);
  const [interviewStyle, setInterviewStyle] = useState('standard');
  const [allQuestions, setAllQuestions] = useState(null); // Store all questions for standalone mode
  const [status, setStatus] = useState({ type: 'ready', message: 'Ready to start' });
  const [transcriptVisible, setTranscriptVisible] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [conversationFeedbackReceived, setConversationFeedbackReceived] = useState(false);
  const [visualFeedbackReceived, setVisualFeedbackReceived] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [sessionData, setSessionData] = useState(null); // Store session data for recreation
  const [currentTranscript, setCurrentTranscript] = useState(''); // Track current transcript for "Next Question" button

  // Refs
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recognitionRef = useRef(null);
  const frameCaptureRef = useRef(null);
  const currentAudioRef = useRef(null);
  const sessionIdRef = useRef(null); // Track sessionId in ref to avoid closure issues
  const currentTranscriptRef = useRef(''); // Track current transcript in ref for button
  const framesCapturedRef = useRef(0); // Track number of frames captured (max 3)
  const capturedFramesRef = useRef([]); // Track base64 frames locally for visual feedback redundancy
  const isListeningRef = useRef(false); // Track listening state in ref to avoid stale closures
  const isProcessingResponseRef = useRef(false); // Track processing state in ref
  const isPausedRef = useRef(false); // Track paused state in ref
  const mediaRecorderRef = useRef(null); // For OpenAI STT (audio capture)
  const audioChunksRef = useRef([]); // Collected audio chunks for transcription
  const sessionDataRef = useRef(null); // Avoid stale closure when process-speech runs from callbacks
  const audioContextRef = useRef(null); // Web Audio API context for robust tablet audio
  const activeAudioSourceRef = useRef(null); // To stop ongoing playback
  const activeUtteranceRef = useRef(null); // Keep reference to SpeechSynthesisUtterance to prevent garbage collection on iOS
  const audioStateRef = useRef({
    paused: false,
    ended: true,
    listeners: {}
  });

  const base64ToArrayBuffer = (base64) => {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const unlockAudio = () => {
    console.log('🔊 Unlocking Web Audio Context for tablet mode...');
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume().then(() => {
          console.log('✅ Web Audio Context resumed/unlocked');
        });
      } else {
        console.log('✅ Web Audio Context is already active');
      }
    } catch (e) {
      console.warn('Failed to initialize AudioContext:', e);
    }
    
    // Unlock SpeechSynthesis fallback
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      try {
        const utterance = new SpeechSynthesisUtterance('');
        window.speechSynthesis.speak(utterance);
        console.log('🗣️ SpeechSynthesis fallback unlocked');
      } catch (e) {
        console.warn('SpeechSynthesis unlock failed:', e);
      }
    }
  };

  const effectiveToolType = !caseStudyContext ? toolType : null;
  const toolConfig = effectiveToolType ? TOOL_TYPE_CONFIG[effectiveToolType] : null;
  const isToolAppMode = effectiveToolType === 'medicine_mmi_interview' ||
    effectiveToolType === 'medicine_panel_interview' ||
    effectiveToolType === 'oxbridge_interview' ||
    effectiveToolType === 'interview_simulator' ||
    effectiveToolType === 'investment_banking_interview' ||
    effectiveToolType === 'private_equity_interview' ||
    effectiveToolType === 'engineering_interview' ||
    effectiveToolType === 'computer_science_interview' ||
    effectiveToolType === 'law_interview' ||
    effectiveToolType === 'operations_fs_interview' ||
    effectiveToolType === 'scholarship_interview';

  // Keep role in sync with any initialRole / tool configuration
  useEffect(() => {
    if (!caseStudyContext) {
      if (initialRole && !role) {
        setRole(initialRole);
      } else if (!initialRole && toolConfig && toolConfig.role && !role) {
        setRole(toolConfig.role);
      }
    }
  }, [caseStudyContext, initialRole, toolConfig, role]);

  // Pause all audio and media when leaving the app (e.g. Pause / Back to home) so nothing runs in background
  useEffect(() => {
    return () => {
      if (currentAudioRef.current) {
        try {
          currentAudioRef.current.pause();
          currentAudioRef.current.currentTime = 0;
          currentAudioRef.current.onended = null;
          currentAudioRef.current.onerror = null;
        } catch (_) {}
        currentAudioRef.current = null;
      }
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach((track) => track.stop());
        } catch (_) {}
        streamRef.current = null;
      }
    };
  }, []);

  // Define autoStartInterview for caseStudyContext (embedded mode)
  const autoStartInterview = async () => {
    try {
      // Wait for speech recognition to be ready
      let retries = 0;
      while (!recognitionRef.current && retries < 10) {
        await new Promise(resolve => setTimeout(resolve, 200));
        retries++;
      }
      
      if (!recognitionRef.current) {
        console.warn('Speech recognition not available after waiting');
        setStatus({ type: 'error', message: 'Speech recognition not supported in this browser. Please use a modern browser with microphone support.' });
        return;
      }

      // Create a minimal CV blob from user info
      const cvContent = `Name: ${userName}
Role: ${userRole}
Email: ${userEmail}
Case Study Context: ${caseStudyContext ? (caseStudyContext.title || 'Case Study Assessment') : ''}

This interview is based on the case study assessment.`;
      
      const cvBlob = new Blob([cvContent], { type: 'text/plain' });
      const autoCvFile = new File([cvBlob], 'cv.txt', { type: 'text/plain' });
      
      // Set the required fields
      setCvFile(autoCvFile);
      setRole(appContext?.title || 'Case Study Assessment');
      setExperienceLevel('Student');
      
      const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      setSessionId(newSessionId);
      sessionIdRef.current = newSessionId;
      console.log('Created new sessionId (auto-start):', newSessionId);

      await initCamera();

      setStatus({ type: 'thinking', message: 'Starting interview...' });

      const formData = new FormData();
      formData.append('cv', autoCvFile);
      formData.append('role', appContext?.title || 'Case Study Assessment');
      formData.append('experience_level', 'Student');
      formData.append('session_id', newSessionId);
      
      if (caseStudyContext) {
        formData.append('case_study_context', JSON.stringify(caseStudyContext));
      }

      const result = await websiteInterviewAPI.startInterview(formData);

      if (result.success !== false && result.opening_question) {
        console.log('Interview started successfully. SessionId:', newSessionId);
        setSessionId(prev => {
          if (prev !== newSessionId) {
            sessionIdRef.current = newSessionId;
            return newSessionId;
          }
          return prev;
        });
        if (sessionIdRef.current !== newSessionId) {
          sessionIdRef.current = newSessionId;
        }
        console.log('SessionId confirmed in state and ref:', sessionIdRef.current);
        
        if (result.session_data) {
          setSessionData(result.session_data);
          sessionDataRef.current = result.session_data;
          console.log('Stored session data for recreation:', result.session_data);
        }
        
        setInterviewStyle(result.interview_style || 'standard');
        if (caseStudyContext) {
          setTotalQuestions(3);
        }
        
        if (result.all_questions && result.all_questions.length >= 3) {
          setAllQuestions(result.all_questions);
          console.log('✅ Stored all 3 questions for standalone processing:', result.all_questions);
        }
        
        const openingQuestion = result.opening_question;
        addToConversationHistory('ai', openingQuestion);
        
        framesCapturedRef.current = 0;
        setTimeout(() => captureFrame(), 1000);
        
        setTimeout(() => {
          speakText(openingQuestion, () => {
            setStatus({ type: 'ready', message: 'Click Start to begin recording your answer' });
          });
        }, 100);

        setCurrentQuestion(1);
      } else {
        const errorMessage = result.error || result.opening_question || 'Failed to start interview';
        console.error('Interview start error:', errorMessage);
        setStatus({ type: 'error', message: errorMessage });
        
        if (result.opening_question) {
          addToConversationHistory('ai', result.opening_question);
        }
      }
    } catch (error) {
      console.error('Error auto-starting interview:', error);
      setStatus({ type: 'error', message: 'Failed to start interview: ' + (error.message || 'Please try again.') });
    }
  };

  // Ensure video stays visible and playing during conversation
  useEffect(() => {
    if (currentView === 'conversation' && videoRef.current && streamRef.current) {
      const video = videoRef.current;
      const stream = streamRef.current;
      
      // Ensure video is visible
      video.style.display = 'block';
      video.style.visibility = 'visible';
      video.style.opacity = '1';
      
      // Ensure video has the stream
      if (!video.srcObject || video.srcObject !== stream) {
        console.log('Restoring video stream');
        video.srcObject = stream;
      }
      
      // Ensure video is playing - especially important when listening starts
      if (video.paused || video.readyState < 2) {
        console.log('Video paused or not ready, attempting to play');
        video.play().catch(err => {
          console.warn('Video play prevented:', err);
        });
      }
      
      // Check stream is active
      const videoTracks = stream.getVideoTracks();
      if (videoTracks.length > 0) {
        if (videoTracks[0].readyState === 'ended') {
          console.warn('Video track ended, reinitializing camera');
          initCamera().catch(err => console.error('Failed to reinitialize camera:', err));
        } else if (videoTracks[0].readyState === 'live' && !video.srcObject) {
          // Stream is live but video lost srcObject - restore it
          console.log('Stream is live but video lost srcObject, restoring');
          video.srcObject = stream;
          video.play().catch(err => console.warn('Video play error after restore:', err));
        }
      }
    }
  }, [currentView, conversationHistory.length, isListening, isProcessingResponse]);

  // Periodic check to ensure video stays visible (runs every 2 seconds during conversation)
  useEffect(() => {
    if (currentView !== 'conversation') return;
    
    const videoCheckInterval = setInterval(() => {
      if (videoRef.current && streamRef.current) {
        const video = videoRef.current;
        const stream = streamRef.current;
        
        // Check if video is dark/not showing
        if (!video.srcObject || video.srcObject !== stream) {
          console.log('Video lost stream, restoring...');
          video.srcObject = stream;
        }
        
        // Ensure video is visible
        if (video.style.display === 'none' || video.style.visibility === 'hidden' || video.style.opacity === '0') {
          console.log('Video hidden, making visible...');
          video.style.display = 'block';
          video.style.visibility = 'visible';
          video.style.opacity = '1';
        }
        
        // Ensure video is playing
        if (video.paused && stream.getVideoTracks().some(track => track.readyState === 'live')) {
          console.log('Video paused but stream is live, playing...');
          video.play().catch(err => {
            console.warn('Video play error in periodic check:', err);
          });
        }
      }
    }, 2000); // Check every 2 seconds
    
    return () => clearInterval(videoCheckInterval);
  }, [currentView]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'speechRecognition' in window) {
      recognitionRef.current = new (window.webkitSpeechRecognition || window.speechRecognition)();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      let finalTranscript = '';
      let processingTimeout = null;
      let hasProcessedFinal = false;
      let processedResultIndices = new Set(); // Track which result indices we've already processed
      
      // Function to reset transcript state
      const resetTranscriptState = () => {
        finalTranscript = '';
        hasProcessedFinal = false;
        processedResultIndices.clear(); // Clear processed indices
        currentTranscriptRef.current = ''; // Clear ref
      setCurrentTranscript(''); // Clear current transcript for button (manual mode)
        if (processingTimeout) {
          clearTimeout(processingTimeout);
          processingTimeout = null;
        }
      };

      recognitionRef.current.onstart = () => {
        // Only used for legacy / non-tool flows; dedicated tools use OpenAI STT
        setIsListening(true);
        isListeningRef.current = true;
        setStatus({ type: 'listening', message: 'Recording... Click Stop when finished' });
        resetTranscriptState(); // Reset state when starting to listen for new answer
        
        // Ensure video stays visible and playing when listening starts
        if (videoRef.current && streamRef.current) {
          const video = videoRef.current;
          const stream = streamRef.current;
          
          // Force video to stay active
          if (!video.srcObject || video.srcObject !== stream) {
            console.log('Restoring video stream in onstart');
            video.srcObject = stream;
          }
          
          video.style.display = 'block';
          video.style.visibility = 'visible';
          video.style.opacity = '1';
          
          if (video.paused) {
            video.play().catch(err => {
              console.warn('Video play error in onstart:', err);
            });
          }
        }
      };

      recognitionRef.current.onresult = (event) => {
        // Update transcript; user will click Next Question to send answer
        // Use ref to avoid stale closure
        if (isProcessingResponseRef.current) {
          return;
        }

        let interimTranscript = '';
        let newFinalTranscript = '';
        let totalTranscript = '';

        if (event && event.results) {
          for (let i = 0; i < event.results.length; i++) {
            const result = event.results[i];
            if (result && result[0] && typeof result[0].transcript === 'string') {
              const transcript = result[0].transcript;
              totalTranscript += transcript;
              
              // Only process final results we haven't seen before
              if (result.isFinal) {
                // Check if we've already processed this result index
                if (!processedResultIndices.has(i)) {
                  newFinalTranscript += transcript;
                  processedResultIndices.add(i); // Mark this index as processed
                }
              } else {
                interimTranscript += transcript;
              }
            }
          }
        }

        // Update final transcript - APPEND only NEW final results (prevent duplicates)
        if (newFinalTranscript) {
          finalTranscript += newFinalTranscript;
          console.log('📝 New final transcript received:', newFinalTranscript, 'Total so far:', finalTranscript);
        }

        // Build total transcript from all results (final + interim)
        let totalText = '';
        if (event && event.results) {
          for (let i = 0; i < event.results.length; i++) {
            const result = event.results[i];
            if (result && result[0] && typeof result[0].transcript === 'string') {
              totalText += result[0].transcript;
            }
          }
        }
        
        // Update current transcript for "Next Question" button (use final transcript if available, otherwise total)
        const transcriptForButton = finalTranscript.trim() || totalText.trim();
        if (transcriptForButton.length >= 3) {
          currentTranscriptRef.current = transcriptForButton;
          setCurrentTranscript(transcriptForButton); // Update state to trigger re-render
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        isListeningRef.current = false;
        
        if (event.error === 'not-allowed') {
          setStatus({ 
            type: 'error', 
            message: 'Microphone permission denied. Please allow microphone access in your browser settings to continue the video interview.' 
          });
          // Only enable text input as last resort when mic is permanently denied
          setShowTextInput(true);
        } else if (event.error === 'network') {
          setStatus({ type: 'paused', message: 'Network connection lost' });
        } else if (event.error === 'no-speech') {
          // This is normal - just means no speech detected yet
          setStatus({ type: 'ready', message: 'Listening...' });
        } else if (event.error === 'aborted') {
          // Aborted is normal when we intentionally stop listening (e.g., when processing response)
          // Don't show error or change state - just log it
          console.log('Speech recognition aborted (this is normal when processing responses)');
        } else {
          setStatus({ 
            type: 'ready', 
            message: 'Ready to continue - microphone will resume automatically' 
          });
          // Don't automatically enable text input for temporary errors
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
        isListeningRef.current = false;
        // Manual mode for sending answers: user clicks Next Question
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (frameCaptureRef.current) {
        clearInterval(frameCaptureRef.current);
      }
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
      }
    };
  }, [isPaused, isProcessingResponse, isListening]);

  const handleRoleChange = (e) => {
    const selectedRole = e.target.value;
    setRole(selectedRole);
    
    if (selectedRole === 'Medicine MMI' || selectedRole === 'Medicine Panel' || selectedRole === 'Oxbridge') {
      setExperienceLevel('Student');
    }
  };

  const scrapeJobDescription = async () => {
    if (!jobUrl.trim()) {
      alert('Please enter a job description URL');
      return;
    }

    try {
      const result = await websiteInterviewAPI.scrapeJob(jobUrl);
      if (result.success) {
        setJobInfo(result.job_info);
      }
    } catch (error) {
      console.error('Error scraping job:', error);
      alert('Failed to analyze job description: ' + error.message);
    }
  };

  const initCamera = async () => {
    try {
      const constraints = {
        video: {
          width: { min: 320, ideal: 640, max: 1280 },
          height: { min: 240, ideal: 480, max: 720 },
          facingMode: 'user'
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        // Clear any existing stream first
        if (videoRef.current.srcObject) {
          const oldStream = videoRef.current.srcObject;
          oldStream.getTracks().forEach(track => track.stop());
        }
        
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.autoplay = true;
        videoRef.current.playsInline = true;
        
        // Ensure video stays visible
        videoRef.current.style.display = 'block';
        videoRef.current.style.visibility = 'visible';
        videoRef.current.style.opacity = '1';
        
        // Wait for video to be ready
        await new Promise((resolve) => {
          if (videoRef.current.readyState >= 2) {
            resolve();
          } else {
            videoRef.current.onloadedmetadata = () => resolve();
          }
        });
        
        // Force play to ensure video is visible
        try {
          await videoRef.current.play();
        } catch (playError) {
          console.warn('Video autoplay prevented, but stream is active:', playError);
        }
        
        // Monitor video stream to ensure it stays active
        stream.getVideoTracks().forEach(track => {
          track.onended = () => {
            console.warn('Video track ended unexpectedly, attempting to reinitialize');
            // Try to reinitialize if track ends unexpectedly
            if (currentView === 'conversation') {
              initCamera().catch(err => console.error('Failed to reinitialize camera:', err));
            }
          };
        });
      }

      // Frame capture will be done manually at specific points (3 total frames)
      // Removed interval-based capture - will capture at: start, after Q1, after Q2
      
      return true;
    } catch (error) {
      console.error('Camera initialization failed:', error);
      alert('Camera access failed: ' + error.message);
      return false;
    }
  };

  const captureFrame = () => {
    // Only capture 3 frames total for the entire interview
    if (framesCapturedRef.current >= 3) {
      console.log('Frame capture limit reached (3 frames), skipping');
      return;
    }

    // Use ref primarily to avoid closure issues
    const currentSessionId = sessionIdRef.current || sessionId;
    if (!streamRef.current || !videoRef.current || !currentSessionId) {
      console.log('Frame capture skipped: missing stream, video, or sessionId');
      return;
    }

    try {
      const video = videoRef.current;
      if (video.readyState < 2) {
        console.log('Frame capture skipped: video not ready');
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const frameData = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
      capturedFramesRef.current.push(frameData);

      // Capture frame and log success/failure for debugging
      framesCapturedRef.current += 1;
      console.log(`📸 Capturing frame ${framesCapturedRef.current}/3`);
      
      websiteInterviewAPI.captureFrame(currentSessionId, frameData)
        .then(() => {
          console.log(`Frame ${framesCapturedRef.current}/3 captured and sent successfully`);
        })
        .catch(error => {
          console.error('Error sending frame:', error);
          framesCapturedRef.current -= 1; // Decrement on error so we can retry
          capturedFramesRef.current.pop();
        });
    } catch (error) {
      console.error('Error capturing frame:', error);
      framesCapturedRef.current -= 1; // Decrement on error so we can retry
      capturedFramesRef.current.pop();
    }
  };

  const speakText = async (text, callback) => {
    // Validate text input
    if (!text || text.trim().length === 0) {
      console.warn('⚠️ speakText called with empty text, skipping');
      if (callback) callback();
      return;
    }
    
    // Stop any current web audio playback
    if (activeAudioSourceRef.current) {
      try {
        activeAudioSourceRef.current.stop();
      } catch (e) {}
      activeAudioSourceRef.current = null;
    }
    audioStateRef.current.ended = true;
    audioStateRef.current.listeners = {};
    currentAudioRef.current = null;
    
    // Also stop browser speech synthesis if running
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    activeUtteranceRef.current = null;

    setStatus({ type: 'speaking', message: 'AI speaking...' });
    console.log(`🗣️ Speaking text (${text.length} chars): ${text.substring(0, 100)}...`);

    const playSpeechSynthesisFallback = (fallbackText, fallbackCallback) => {
      audioStateRef.current = {
        paused: false,
        ended: false,
        listeners: {}
      };
      
      currentAudioRef.current = {
        get paused() { return audioStateRef.current.paused; },
        get ended() { return audioStateRef.current.ended; },
        pause: () => {
          audioStateRef.current.paused = true;
          if (typeof window !== 'undefined' && window.speechSynthesis) {
            window.speechSynthesis.pause();
          }
        },
        play: () => {
          audioStateRef.current.paused = false;
          if (typeof window !== 'undefined' && window.speechSynthesis) {
            window.speechSynthesis.resume();
          }
        },
        addEventListener: (event, cb) => {
          if (!audioStateRef.current.listeners[event]) {
            audioStateRef.current.listeners[event] = [];
          }
          audioStateRef.current.listeners[event].push(cb);
        },
        removeEventListener: (event, cb) => {
          if (audioStateRef.current.listeners[event]) {
            audioStateRef.current.listeners[event] = audioStateRef.current.listeners[event].filter(l => l !== cb);
          }
        },
        set currentTime(val) {},
        get currentTime() { return 0; },
        set onended(val) {},
        set onerror(val) {}
      };

      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(fallbackText);
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        utterance.volume = 1.0;
        
        // Prevent iOS Safari garbage collection bug
        activeUtteranceRef.current = utterance;

        const handleEnded = () => {
          audioStateRef.current.ended = true;
          if (activeUtteranceRef.current === utterance) {
            activeUtteranceRef.current = null;
          }
          console.log('✅ Fallback speech synthesis completed');
          
          // Trigger ended event listeners
          const listeners = audioStateRef.current.listeners['ended'] || [];
          listeners.forEach(cb => {
            try {
              cb();
            } catch (e) {
              console.error('Error in ended listener:', e);
            }
          });

          if (fallbackCallback) fallbackCallback();
        };

        utterance.onend = handleEnded;
        utterance.onerror = (e) => {
          console.error('Speech synthesis error:', e);
          handleEnded();
        };
        window.speechSynthesis.speak(utterance);
      } else {
        audioStateRef.current.ended = true;
        if (fallbackCallback) fallbackCallback();
      }
    };

    try {
      const result = await websiteInterviewAPI.generateSpeech(text);
      
      if (result.success) {
        // Initialize/resume AudioContext
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const ctx = audioContextRef.current;
        if (ctx.state === 'suspended') {
          await ctx.resume();
        }

        const arrayBuf = base64ToArrayBuffer(result.audio_data);

        ctx.decodeAudioData(arrayBuf, (audioBuffer) => {
          const source = ctx.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(ctx.destination);
          
          activeAudioSourceRef.current = source;
          audioStateRef.current = {
            paused: false,
            ended: false,
            listeners: {}
          };

          // Setup currentAudioRef shim for play/pause compatibility checks
          currentAudioRef.current = {
            get paused() { return audioStateRef.current.paused; },
            get ended() { return audioStateRef.current.ended; },
            pause: () => {
              audioStateRef.current.paused = true;
              if (audioContextRef.current) {
                audioContextRef.current.suspend();
              }
            },
            play: () => {
              audioStateRef.current.paused = false;
              if (audioContextRef.current) {
                audioContextRef.current.resume();
              }
            },
            addEventListener: (event, cb) => {
              if (!audioStateRef.current.listeners[event]) {
                audioStateRef.current.listeners[event] = [];
              }
              audioStateRef.current.listeners[event].push(cb);
            },
            removeEventListener: (event, cb) => {
              if (audioStateRef.current.listeners[event]) {
                audioStateRef.current.listeners[event] = audioStateRef.current.listeners[event].filter(l => l !== cb);
              }
            },
            set currentTime(val) {},
            get currentTime() { return 0; },
            set onended(val) {},
            set onerror(val) {}
          };

          source.onended = () => {
            audioStateRef.current.ended = true;
            if (activeAudioSourceRef.current === source) {
              activeAudioSourceRef.current = null;
            }
            console.log('✅ Audio playback completed');

            // Trigger ended event listeners
            const listeners = audioStateRef.current.listeners['ended'] || [];
            listeners.forEach(cb => {
              try {
                cb();
              } catch (e) {
                console.error('Error in ended listener:', e);
              }
            });

            if (callback) callback();
          };

          source.start(0);
        }, (err) => {
          console.error('Error decoding audio data:', err);
          playSpeechSynthesisFallback(text, callback);
        });
      } else {
        playSpeechSynthesisFallback(text, callback);
      }
    } catch (error) {
      console.error('Error with speech generation:', error);
      playSpeechSynthesisFallback(text, callback);
    }
  };

  const base64ToBlob = (base64Data, contentType) => {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  };

  // OpenAI STT recording helpers (dedicated tools only)
  const startOpenAIRecording = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('Media devices API not available');
      setStatus({ type: 'error', message: 'Microphone not available in this browser.' });
      return;
    }

    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(audioStream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        try {
          const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          audioChunksRef.current = [];
          setStatus({ type: 'thinking', message: 'Transcribing your answer...' });

          const result = await websiteInterviewAPI.transcribeAudioBlob(blob);
          if (!result.success || !result.text) {
            console.error('Transcription failed:', result);
            setStatus({ type: 'error', message: result.error || 'Failed to transcribe audio' });
            setIsProcessingResponse(false);
            isProcessingResponseRef.current = false;
            return;
          }

          const transcript = result.text.trim();
          if (!transcript) {
            setStatus({ type: 'error', message: 'No speech detected. Please try again.' });
            setIsProcessingResponse(false);
            isProcessingResponseRef.current = false;
            return;
          }

          // Feed transcribed text into the normal response pipeline
          await processUserResponse(transcript);
        } catch (err) {
          console.error('Error during transcription pipeline:', err);
          setStatus({ type: 'error', message: 'Failed to process your answer. Please try again.' });
          setIsProcessingResponse(false);
          isProcessingResponseRef.current = false;
        } finally {
          // Stop all tracks on the stream
          if (mediaRecorderRef.current && mediaRecorderRef.current.stream) {
            mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
          }
          mediaRecorderRef.current = null;
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setStatus({ type: 'listening', message: 'Recording... answer when you are ready, then click Next Question' });
    } catch (err) {
      console.error('Failed to start OpenAI recording:', err);
      setStatus({ type: 'error', message: 'Could not access microphone. Please check permissions.' });
    }
  };

  const stopOpenAIRecordingAndTranscribe = () => {
    if (!mediaRecorderRef.current) return;
    try {
      mediaRecorderRef.current.stop();
    } catch (err) {
      console.error('Error stopping MediaRecorder:', err);
    }
  };

  const startConversation = async () => {
    unlockAudio();
    const isStudentOnlyTool = !!(toolConfig && toolConfig.role);
    if (!cvFile || !role) {
      alert('Please fill in all required fields');
      return;
    }
    if (!isStudentOnlyTool && !experienceLevel) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      // Set both state and ref immediately
      sessionIdRef.current = newSessionId;
      setSessionId(newSessionId);

      const speechReady = recognitionRef.current !== null;
      const cameraReady = await initCamera();

      if (!speechReady) {
        alert('Speech recognition not supported in this browser');
        return;
      }

      setCurrentView('conversation');
      setStatus({ type: 'thinking', message: 'Starting interview...' });

      const formData = new FormData();
      formData.append('cv', cvFile);
      formData.append('role', role);
      formData.append('experience_level', isStudentOnlyTool ? 'Student' : experienceLevel);
      formData.append('session_id', newSessionId);
      
      if (jobInfo) {
        formData.append('job_info', JSON.stringify(jobInfo));
      }
      
      // Add case study context if available (for app experience personalization)
      if (caseStudyContext) {
        formData.append('case_study_context', JSON.stringify(caseStudyContext));
      }

      const result = await websiteInterviewAPI.startInterview(formData);

      if (result.success) {
        // Store session data for potential recreation (state + ref so callbacks always have it)
        if (result.session_data) {
          setSessionData(result.session_data);
          sessionDataRef.current = result.session_data;
          console.log('Stored session data for recreation');
        }
        
        setInterviewStyle(result.interview_style || 'standard');
        const openingQuestion = result.opening_question;
        
        addToConversationHistory('ai', openingQuestion);
        
        setTimeout(() => {
          speakText(openingQuestion, () => {
            if (isToolAppMode) {
              // Dedicated tools: start OpenAI STT recording
              startOpenAIRecording();
            } else {
              setStatus({ type: 'ready', message: 'Your turn to speak' });
              startListening();
            }
          });
        }, 100);

        setCurrentQuestion(1);
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      alert('Failed to start interview: ' + error.message);
    }
  };

  const processUserResponse = async (userMessage) => {
    if (isProcessingResponse) {
      console.log('Already processing response, ignoring duplicate');
      return;
    }

    // CRITICAL: Check if audio is currently playing - wait for it to finish
    // This prevents interrupting the AI's question mid-speech (applies to all questions)
    if (currentAudioRef.current && !currentAudioRef.current.paused && !currentAudioRef.current.ended) {
      console.log('⏳ Audio still playing, waiting for it to finish before processing response...');
      return new Promise((resolve) => {
        currentAudioRef.current.addEventListener('ended', () => {
          console.log('✅ Audio finished, now processing response');
          setTimeout(() => {
            processUserResponse(userMessage).then(resolve);
          }, 300); // Small buffer after audio ends
        }, { once: true });
      });
    }

    // Ensure sessionId is synchronized - ref should be source of truth
    if (sessionIdRef.current && !sessionId) {
      console.log('Syncing sessionId state from ref:', sessionIdRef.current);
      setSessionId(sessionIdRef.current);
    } else if (sessionId && !sessionIdRef.current) {
      console.log('Syncing sessionId ref from state:', sessionId);
      sessionIdRef.current = sessionId;
    }

    // CRITICAL: Stop listening FIRST and ensure it's stopped
    stopListening();
    
    // Set processing flag immediately (before async operations) for better UI responsiveness
    setIsProcessingResponse(true);
    isProcessingResponseRef.current = true;
    
    // Wait a moment to ensure speech recognition is fully stopped
    await new Promise(resolve => setTimeout(resolve, 100)); // Reduced delay
    
    // Capture a frame when user responds (important moment for analysis)
    captureFrame();
    
    setStatus({ type: 'thinking', message: 'Processing your response...' });
    addToConversationHistory('user', userMessage);

    try {
      // Always use the ref value as source of truth to avoid stale closures
      // If ref is empty, try to sync from state, but ref should always be set
      let activeSessionId = sessionIdRef.current;
      
      // If ref is empty but state has it, sync ref from state
      if (!activeSessionId && sessionId) {
        console.warn('Ref empty but state has sessionId, syncing...');
        sessionIdRef.current = sessionId;
        activeSessionId = sessionId;
      }
      
      // If still empty, we have a problem
      if (!activeSessionId) {
        console.error('No sessionId available! State:', sessionId, 'Ref:', sessionIdRef.current);
        setStatus({ type: 'error', message: 'Session lost. Please restart the interview.' });
        setIsProcessingResponse(false);
        return;
      }
      
      // Ensure state is also set (for consistency)
      if (sessionId !== activeSessionId) {
        console.log('Syncing sessionId state from ref:', activeSessionId);
        setSessionId(activeSessionId);
      }
      
      console.log('Processing response - sessionId state:', sessionId, 'sessionId ref:', sessionIdRef.current, 'using:', activeSessionId);
      console.log('User message:', userMessage);
      
      // For case-study (experience app): use fixed all_questions. For MMI/Panel/Oxbridge tool apps: no all_questions — flowing session mode with full transcript.
      const effectiveSessionData = sessionDataRef.current ?? sessionData;
      let updatedSessionData = effectiveSessionData;
      if (effectiveSessionData && !allQuestions) {
        updatedSessionData = {
          ...effectiveSessionData,
          current_question_index: currentQuestion - 1,
          questions_asked: currentQuestion,
          // Rebuild conversation_history in the shape the backend expects:
          // each entry is a full exchange with user_message and ai_message.
          conversation_history: conversationHistory.map((item) => ({
            user_message: item.speaker === 'user' ? item.message : '',
            ai_message: item.speaker === 'ai' ? item.message : '',
            timestamp: item.timestamp || new Date().toISOString()
          }))
        };
      }
      
      // Standalone mode: send all questions and current question number
      console.log('🔍 Processing with:', {
        allQuestions: allQuestions ? `${allQuestions.length} questions` : 'null',
        currentQuestion,
        standaloneMode: !!allQuestions
      });
      
      const result = await websiteInterviewAPI.processSpeech(
        activeSessionId, 
        userMessage, 
        allQuestions ? null : updatedSessionData, // No session data in standalone mode
        allQuestions, // Send all questions
        currentQuestion, // Current question number (1-based)
        role || toolConfig?.role || null // The page's role, for the server's package gate
      );
      
      console.log('Backend response:', result);

      if (result.interview_complete || (currentQuestion >= totalQuestions && allQuestions)) {
        console.log('Interview complete, ending...');
        // Clear transcript before ending
        currentTranscriptRef.current = '';
        setCurrentTranscript('');
        // Add completion message to history if provided
        if (result.ai_response) {
          addToConversationHistory('ai', result.ai_response);
        }
        endInterview();
        return;
      }

      if (!result.success) {
        console.error('Backend returned error:', result.error);
        setStatus({ type: 'error', message: result.error || 'Failed to process response' });
        setIsProcessingResponse(false);
        isProcessingResponseRef.current = false;
        return;
      }

      const aiResponse = result.ai_response;
      console.log('AI response received:', aiResponse ? `${aiResponse.substring(0, 100)}... (${aiResponse.length} chars)` : 'null');
      
      if (!aiResponse) {
        console.error('No AI response in result:', result);
        setStatus({ type: 'error', message: 'No response received from server' });
        setIsProcessingResponse(false);
        return;
      }
      
      addToConversationHistory('ai', aiResponse);
      
      // Clear current transcript after processing (will be reset when user starts recording next answer)
      // Don't clear immediately - let it show until next question starts
      
      // Update question number - drive from backend response when available,
      // fall back to a safe local increment. Use functional update so we
      // always base on the latest state.
      setCurrentQuestion((prevQuestion) => {
        if (typeof result.current_question === 'number') {
          console.log(`📝 Updating question number from ${prevQuestion} to ${result.current_question}`);
          return result.current_question;
        }
        if (typeof result.questions_asked === 'number') {
          console.log(`📝 Updating question number from ${prevQuestion} to ${result.questions_asked} (from questions_asked)`);
          return result.questions_asked;
        }
        const nextQuestion = prevQuestion + 1;
        console.log(`📝 Updating question number from ${prevQuestion} to ${nextQuestion} (incremented fallback)`);
        return nextQuestion;
      });
      
      // Don't clear processing flag here - wait until AI finishes speaking to prevent cutting off

      // Small delay before speaking to ensure previous audio is cleared and speech recognition stops
      setTimeout(() => {
        // Ensure we stop listening before AI speaks - do this thoroughly
        stopListening();
        
        // Wait a bit to ensure speech recognition is fully stopped
        setTimeout(() => {
          // Double-check listening is stopped
          if (recognitionRef.current) {
            try {
              if (isListeningRef.current) {
                recognitionRef.current.stop();
              }
              recognitionRef.current.abort(); // Force abort to ensure it's stopped
            } catch (e) {
              console.warn('Error stopping recognition before speaking:', e);
            }
          }
          
          // Ensure audio completes fully before restarting - add extra delay
          // Check if this is the final question - if so, don't restart listening after user answers
          const nextQuestionAfterThis = result.current_question || (currentQuestion + 1);
          const willBeFinalQuestion = nextQuestionAfterThis >= totalQuestions;
          
          console.log(`📊 Question info: current=${currentQuestion}, next=${nextQuestionAfterThis}, total=${totalQuestions}, willBeFinal=${willBeFinalQuestion}`);
          console.log(`🎤 About to speak question ${nextQuestionAfterThis} (${willBeFinalQuestion ? 'FINAL' : 'not final'})`);
          
          // For final question, ensure we don't interrupt the audio
          // Store a flag to prevent any cleanup during final question playback
          const isFinalQuestionPlayback = willBeFinalQuestion;
          
          speakText(aiResponse, () => {
            // Callback fires when audio ends
            console.log('✅ Audio callback fired');
            console.log(`📊 State check: isPaused=${isPausedRef.current}, isProcessing=${isProcessingResponseRef.current}, isListening=${isListeningRef.current}`);
            
            // Clear processing flag IMMEDIATELY for better button responsiveness
            setIsProcessingResponse(false);
            isProcessingResponseRef.current = false;
            
            // Add small buffer for status message
            setTimeout(() => {
              if (isToolAppMode) {
                // Dedicated interview tools: use OpenAI STT via MediaRecorder
                if (!isPausedRef.current && !isProcessingResponseRef.current) {
                  console.log('🎤 Auto mode: Starting OpenAI recording for dedicated interview tools');
                  startOpenAIRecording();
                }
              } else {
                setStatus({ type: 'ready', message: 'Click Start to begin recording your answer' });
                
                // MANUAL MODE: Don't auto-restart listening - user controls with Start/Stop buttons
                console.log('🎤 Manual mode: Waiting for user to click Start button');
              }
            }, 100); // Reduced delay for faster UI response
          });
        }, 200); // Small delay to ensure speech recognition is stopped
      }, 400); // Delay to ensure cleanup
    } catch (error) {
      console.error('Error processing response:', error);
      console.error('Current sessionId state:', sessionId, 'ref:', sessionIdRef.current);
      const errorMessage = error.message || 'Failed to process response';
      setStatus({ type: 'error', message: 'Error: ' + errorMessage });
      
      // If it's a session error, provide clear feedback and suggestion
      if (errorMessage.includes('session') || errorMessage.includes('Invalid')) {
        console.error('Session error detected. Current sessionId:', sessionId, 'ref:', sessionIdRef.current);
        console.error('Error details:', error);
        setIsProcessingResponse(false);
        isProcessingResponseRef.current = false;
        
        // Show user-friendly error message
        setStatus({ 
          type: 'error', 
          message: 'Session error: The interview session was lost. Please restart the interview to continue.' 
        });
        
        // Don't try to restart listening - user needs to restart the interview
      } else {
        setIsProcessingResponse(false);
        setTimeout(() => {
          if (!isPaused && !isProcessingResponse) {
            startListening();
          }
        }, 2000);
      }
    }
  };

  const startListening = () => {
    // Legacy/browser STT start. Dedicated tools now use OpenAI STT via MediaRecorder.
    if (!recognitionRef.current) {
      console.warn('Speech recognition not initialized');
      return;
    }
    
    if (isListeningRef.current || isPausedRef.current || isProcessingResponseRef.current) {
      return;
    }
    
    setIsListening(true);
    isListeningRef.current = true;
    
    if (videoRef.current && streamRef.current) {
      const video = videoRef.current;
      const stream = streamRef.current;
      
      if (!video.srcObject || video.srcObject !== stream) {
        console.log('Restoring video stream before listening');
        video.srcObject = stream;
      }
      
      if (video.paused) {
        video.play().catch(err => {
          console.warn('Video play error when starting to listen:', err);
        });
      }
    }
    
    try {
      recognitionRef.current.start();
    } catch (err) {
      console.error('Error starting recognition:', err);
      setIsListening(false);
      isListeningRef.current = false;
    }
  };

  const stopListening = () => {
    // For legacy/browser STT only – dedicated tools now use OpenAI STT via MediaRecorder
    setIsListening(false);
    isListeningRef.current = false;
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (err) {
        console.log('Recognition stop (this is normal):', err.message);
      }
      
      try {
        recognitionRef.current.abort();
      } catch (err) {
        console.log('Recognition abort (this is normal):', err.message);
      }
    }
  };

  const togglePause = () => {
    const newPaused = !isPaused;
    setIsPaused(newPaused);
    isPausedRef.current = newPaused;
    
    if (newPaused) {
      stopListening();
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
      }
      setStatus({ type: 'paused', message: 'Interview paused' });
    } else {
      setStatus({ type: 'ready', message: 'Interview resumed' });
      if (currentAudioRef.current && currentAudioRef.current.paused) {
        currentAudioRef.current.play();
      } else if (!isProcessingResponse) {
        startListening();
      }
    }
  };

  const endInterview = async () => {
    setIsPaused(true);
    setIsProcessingResponse(true);
    stopListening();
    
    if (frameCaptureRef.current) {
      clearInterval(frameCaptureRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    setCurrentView('feedback');

    // Load both feedbacks in parallel for faster response
    loadConversationFeedback();
    loadVisualFeedback(); // Load immediately in parallel
  };

  const loadConversationFeedback = async () => {
    try {
      const currentSessionId = sessionId || sessionIdRef.current;
      if (!currentSessionId) {
        console.error('No sessionId for feedback');
        return;
      }
      // Build conversation_history as Q/A pairs (backend expects each exchange: { ai_message, user_message })
      const buildConversationExchanges = (history) => {
        const exchanges = [];
        for (let i = 0; i < history.length; i += 2) {
          const aiItem = history[i];
          const userItem = history[i + 1];
          exchanges.push({
            ai_message: aiItem?.speaker === 'ai' ? aiItem.message : (aiItem?.message || ''),
            user_message: userItem?.speaker === 'user' ? userItem.message : (userItem?.message || ''),
            timestamp: (userItem || aiItem)?.timestamp || new Date().toISOString()
          });
        }
        return exchanges;
      };
      const effectiveSessionData = sessionDataRef.current || sessionData;
      const payloadSessionData = effectiveSessionData
        ? {
            ...effectiveSessionData,
            conversation_history: buildConversationExchanges(conversationHistory),
            questions_asked: currentQuestion,
            max_questions: totalQuestions
          }
        : {
            session_id: currentSessionId,
            conversation_history: buildConversationExchanges(conversationHistory),
            questions_asked: currentQuestion,
            max_questions: totalQuestions,
            role: role || toolConfig?.role || 'Oxbridge',
            experience_level: 'Student',
            cv_content: '',
            interview_style: interviewStyle || 'standard'
          };
      const result = await websiteInterviewAPI.generateConversationFeedback(currentSessionId, payloadSessionData);
      
      if (result.success) {
        setFeedback(prev => ({
          ...prev,
          conversationAnalysis: result.conversation_analysis,
          interviewSummary: result.interview_summary
        }));
        setConversationFeedbackReceived(true);
        
        // Don't automatically call onComplete - wait for user to click the button
        // This ensures they can see and read the feedback before moving on
      }
    } catch (error) {
      console.error('Error loading conversation feedback:', error);
      // Even on error, don't auto-call onComplete - let user click button
      setConversationFeedbackReceived(true); // Mark as received so button shows
    }
  };

  const loadVisualFeedback = async () => {
    try {
      const currentSessionId = sessionId || sessionIdRef.current;
      if (!currentSessionId) {
        console.error('No sessionId for visual feedback');
        return;
      }
      const result = await websiteInterviewAPI.generateVisualFeedback(currentSessionId, capturedFramesRef.current);
      
      if (result.success) {
        setFeedback(prev => ({
          ...prev,
          visualPresentation: result.visual_presentation
        }));
        setVisualFeedbackReceived(true);
        
        // Visual feedback is loaded, but onComplete is already scheduled in loadConversationFeedback
        // No need to call it again here to avoid double-calling
      }
    } catch (error) {
      console.error('Error loading visual feedback:', error);
      // Visual feedback failure doesn't prevent completion - already handled in loadConversationFeedback
    }
  };

  const addToConversationHistory = (speaker, message) => {
    setConversationHistory(prev => [...prev, { speaker, message, timestamp: new Date().toISOString() }]);
  };

  // Feedback quotes the candidate's transcript, so the result is sanitized.
  const formatAsBulletPoints = (text) => {
    if (!text || typeof text !== 'string') return sanitizeHtml(text || '');

    const cleanText = text.replace(/[*-]/g, '').trim();

    if (cleanText.length < 100) {
      return sanitizeHtml(cleanText);
    }

    const sentences = cleanText.split(/(?<=[.!?])\s+/).filter(s => s.trim().length > 10);

    if (sentences.length <= 1) {
      return sanitizeHtml(cleanText);
    }

    const bulletPoints = sentences.map(sentence => {
      const trimmed = sentence.trim().replace(/[.!?]+$/, '');
      return trimmed ? `<li>${trimmed}</li>` : '';
    }).filter(bullet => bullet);

    return sanitizeHtml(bulletPoints.length > 1 ? `<ul>${bulletPoints.join('')}</ul>` : cleanText);
  };

  const restartInterview = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    if (frameCaptureRef.current) clearInterval(frameCaptureRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
    
    // Reset state
    setSessionId(null);
    sessionIdRef.current = null; // Also clear ref
    setIsListening(false);
    setIsPaused(false);
    setIsProcessingResponse(false);
    setConversationHistory([]);
    setCurrentQuestion(0);
    setJobInfo(null);
    setTranscriptVisible(false);
    setConversationFeedbackReceived(false);
    setVisualFeedbackReceived(false);
    setFeedback(null);
    setCurrentView('setup');
    
    // Reset form
    setCvFile(null);
    setRole('');
    setExperienceLevel('');
    setJobUrl('');
  };

  const downloadReport = () => {
    const report = {
      session_id: sessionId,
      conversation_history: conversationHistory,
      feedback: feedback,
      timestamp: new Date().toISOString(),
      conversation_feedback_received: conversationFeedbackReceived,
      visual_feedback_received: visualFeedbackReceived
    };
    
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `interview-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Hide header when embedded in app experience
  const isEmbedded = !!caseStudyContext;

  const stopMediaForBack = () => {
    if (currentAudioRef.current) {
      try {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current.onended = null;
        currentAudioRef.current.onerror = null;
      } catch (_) {}
      currentAudioRef.current = null;
    }
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    if (activeAudioSourceRef.current) {
      try {
        activeAudioSourceRef.current.stop?.();
      } catch (_) {}
      activeAudioSourceRef.current = null;
    }
  };

  // One level: interview/feedback → setup → Apps
  const handleBack = useCallback(() => {
    if (currentView === 'conversation' || currentView === 'feedback') {
      stopMediaForBack();
      if (streamRef.current) {
        try {
          streamRef.current.getTracks().forEach((track) => track.stop());
        } catch (_) {}
        streamRef.current = null;
      }
      setCurrentView('setup');
      return;
    }
    navigate('/apps');
  }, [currentView, navigate]);

  useProductBackHandler(isEmbedded ? null : handleBack);

  return (
    <div className="website-interview-page">
      <main className={isEmbedded ? "website-interview-main embedded" : "website-interview-main"}>
        {currentView === 'setup' && !caseStudyContext && (
          <div className="setup-section">
            <h2>Interview Setup</h2>
            
            <div className="form-group">
              <label htmlFor="cvInput">
                {toolConfig && toolConfig.role ? 'Upload your personal statement (PDF)' : 'Upload your CV/Resume (PDF)'}
              </label>
              <input 
                type="file" 
                id="cvInput" 
                accept=".pdf" 
                required
                onChange={(e) => setCvFile(e.target.files[0])}
              />
            </div>

            {toolConfig && toolConfig.role ? (
              <div className="form-group">
                <label>Interview Type</label>
                <div className="readonly-field">
                  {toolConfig.label}
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="careerStream">Career Stream/Position</label>
                <select 
                  id="careerStream" 
                  required
                  value={role}
                  onChange={handleRoleChange}
                >
                  <option value="">Select your target role...</option>
                  {ROLE_OPTIONS.filter((opt) => {
                    // In generic Interview Simulator mode, hide dedicated medicine/oxbridge options
                    if (effectiveToolType === 'interview_simulator') {
                      if (opt.value === 'Medicine MMI' || opt.value === 'Medicine Panel' || opt.value === 'Oxbridge') {
                        return false;
                      }
                    }
                    return true;
                  }).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Experience level: only for Interview Simulator / generic; MMI/Panel/Oxbridge are Student-only */}
            {!(toolConfig && toolConfig.role) && (
            <div className="form-group">
              <label htmlFor="experienceLevel">Experience Level</label>
              <select 
                id="experienceLevel" 
                required
                value={experienceLevel}
                onChange={(e) => setExperienceLevel(e.target.value)}
              >
                <option value="">Select your experience level...</option>
                <option value="Student">Student</option>
                <option value="Intern">Intern</option>
                <option value="Entry Level">Entry Level (0-2 years)</option>
                <option value="Mid Level">Mid Level (3-5 years)</option>
                <option value="Senior Level">Senior Level (6+ years)</option>
                <option value="Executive Level">Executive Level</option>
              </select>
            </div>
            )}

            {/* For dedicated medicine / Oxbridge apps, keep setup simple: no job URL analysis */}
            {!(toolConfig && toolConfig.role) && (
              <>
                <div className="form-group">
                  <label htmlFor="jobUrl">Job Description / University Course URL (Optional)</label>
                  <input 
                    type="url" 
                    id="jobUrl" 
                    placeholder="https://company.com/job-posting OR https://university.edu/course-info"
                    value={jobUrl}
                    onChange={(e) => setJobUrl(e.target.value)}
                  />
                  <small>Add a job posting URL or university course page to get tailored questions for the specific role/program</small>
                </div>

                {jobInfo && (
                  <div className="job-info-display">
                    <h4>Content Information Extracted</h4>
                    <div className="job-info-content">
                      {jobInfo.university_name ? (
                        <>
                          <div><strong>University:</strong> {jobInfo.university_name || 'N/A'}</div>
                          <div><strong>Course:</strong> {jobInfo.course_title || 'N/A'}</div>
                          <div><strong>Degree Level:</strong> {jobInfo.degree_level || 'N/A'}</div>
                          <div><strong>Key Subjects:</strong> {jobInfo.key_subjects ? jobInfo.key_subjects.slice(0, 3).join(', ') + (jobInfo.key_subjects.length > 3 ? '...' : '') : 'N/A'}</div>
                        </>
                      ) : (
                        <>
                          <div><strong>Company:</strong> {jobInfo.company_name || 'N/A'}</div>
                          <div><strong>Position:</strong> {jobInfo.job_title || 'N/A'}</div>
                          <div><strong>Key Skills:</strong> {jobInfo.key_skills ? jobInfo.key_skills.slice(0, 3).join(', ') + (jobInfo.key_skills.length > 3 ? '...' : '') : 'N/A'}</div>
                          <div><strong>Level:</strong> {jobInfo.experience_level || 'N/A'}</div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="form-actions">
              {!(toolConfig && toolConfig.role) && jobUrl.trim() && (
                <button className="btn btn-secondary" onClick={scrapeJobDescription}>
                  Analyze Content
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={startConversation}
                style={{ background: 'var(--iv-btn)', borderColor: 'var(--iv-head)', color: 'var(--iv-btn-fg)' }}
              >
                Start Interview
              </button>
            </div>
          </div>
        )}

        {currentView === 'conversation' && !sessionId && (
          <div className="setup-section embedded-setup" style={{ textAlign: 'center', padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
            <h2 style={{ fontSize: '28px', marginBottom: '16px', fontFamily: 'Outfit, sans-serif' }}>Video Interview</h2>
            <p style={{ maxWidth: '600px', margin: '0 auto 30px auto', color: 'var(--iv-muted)', lineHeight: '1.6', fontSize: '16px' }}>
              Welcome to the interactive video interview. You will be asked {totalQuestions} questions based on the case study assessment. Make sure your camera and microphone are connected.
            </p>
            <div className="form-actions" style={{ justifyContent: 'center', width: '100%' }}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  unlockAudio();
                  autoStartInterview();
                }}
                style={{ 
                  background: 'var(--iv-btn)', 
                  borderColor: 'var(--iv-head)', 
                  color: 'var(--iv-btn-fg)',
                  fontSize: '18px',
                  padding: '16px 32px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Start Interview
              </button>
            </div>
          </div>
        )}

        {currentView === 'conversation' && sessionId && (
          <div className="conversation-section">
            <div className="video-container">
              <video 
                ref={videoRef} 
                id="userVideo" 
                autoPlay 
                muted 
                playsInline
                style={{ 
                  display: 'block',
                  visibility: 'visible',
                  opacity: 1,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              ></video>
              {isListening && (
                <div className="listening-indicator">
                  <div className="listening-pulse"></div>
                  Listening
                </div>
              )}
            </div>
            
            <div className="conversation-display">
              {conversationHistory.length > 0 
                ? conversationHistory[conversationHistory.length - 1].message
                : "Welcome! I'm preparing your personalized interview questions..."
              }
            </div>

            {/* Text input fallback when microphone is not available */}
            {showTextInput && (
              <div className="text-input-fallback" style={{ 
                padding: '20px', 
                background: 'var(--iv-bg)', 
                borderRadius: '8px', 
                marginTop: '20px',
                marginBottom: '20px'
              }}>
                <p style={{ marginBottom: '10px', fontSize: '14px', color: 'var(--iv-muted)' }}>
                  Microphone access is not available. Please type your response below:
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && textInput.trim() && !isProcessingResponse) {
                        processUserResponse(textInput.trim());
                        setTextInput('');
                      }
                    }}
                    placeholder="Type your answer here and press Enter..."
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: '1px solid var(--iv-line)',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                    disabled={isProcessingResponse}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      if (textInput.trim() && !isProcessingResponse) {
                        processUserResponse(textInput.trim());
                        setTextInput('');
                      }
                    }}
                    disabled={!textInput.trim() || isProcessingResponse}
                  >
                    Send
                  </button>
                </div>
              </div>
            )}

            <div className="conversation-controls">
              <div className="status-display">
                <span className={`status-indicator status-${status.type}`}>
                  {status.message}
                </span>
              </div>
              
              <div className="progress-info">
                Question {currentQuestion} of {totalQuestions} • {interviewStyle === 'mmi' ? 'Continue with scenario' : 'Record your answer, then click Next Question'}
              </div>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {/* Start/Stop Recording Buttons (hidden for dedicated interview tools; they auto-record) */}
                {!isToolAppMode && !isListening && !isProcessingResponseRef.current && (
                  <button 
                    className="btn btn-primary" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      unlockAudio();
                      // Use refs for immediate checks
                      if (!isPausedRef.current && !isProcessingResponseRef.current && recognitionRef.current) {
                        startListening();
                        setStatus({ type: 'listening', message: 'Recording... Click Stop when finished' });
                      }
                    }}
                    disabled={isPaused || isProcessingResponseRef.current}
                    style={{ 
                      background: 'var(--iv-success)',
                      color: 'var(--iv-btn-fg)',
                      fontWeight: '600',
                      fontSize: '16px',
                      padding: '14px 24px',
                      minWidth: '120px',
                      cursor: (isPaused || isProcessingResponseRef.current) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ▶ Start
                  </button>
                )}
                {!isToolAppMode && isListening && (
                  <button 
                    className="btn btn-danger" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      stopListening();
                      setIsListening(false); // Update state immediately
                      setStatus({ type: 'ready', message: 'Recording stopped. Click Next Question to continue' });
                    }}
                    disabled={isProcessingResponseRef.current}
                    style={{ 
                      background: 'var(--iv-danger)',
                      color: 'var(--iv-btn-fg)',
                      fontWeight: '600',
                      fontSize: '16px',
                      padding: '14px 24px',
                      minWidth: '120px',
                      cursor: isProcessingResponseRef.current ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ⏹ Stop
                  </button>
                )}
                
                {/* Next Question / End Question Button */}
                {(!isToolAppMode && currentTranscript && currentTranscript.length >= 3 && !isProcessingResponseRef.current) || 
                  (isToolAppMode && !isProcessingResponseRef.current && mediaRecorderRef.current) ? (
                  <button 
                    className="btn btn-primary" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      unlockAudio();
                      
                      // Prevent double-clicks
                      if (isProcessingResponseRef.current) {
                        return;
                      }

                      if (isToolAppMode) {
                        // Dedicated tools: stop recording and let OpenAI STT + processUserResponse handle the rest
                        setIsProcessingResponse(true);
                        isProcessingResponseRef.current = true;
                        stopOpenAIRecordingAndTranscribe();
                      } else {
                        const transcriptToUse = currentTranscriptRef.current || currentTranscript;
                        
                        // Clear transcript before processing
                        currentTranscriptRef.current = '';
                        setCurrentTranscript('');
                        
                        // If this is the last question, the backend will mark interview as complete
                        processUserResponse(transcriptToUse);
                      }
                    }}
                    disabled={isProcessingResponseRef.current || (!isToolAppMode && isListeningRef.current)}
                    style={{ 
                      background: currentQuestion >= totalQuestions ? 'var(--iv-accent)' : 'var(--iv-accent)',
                      color: 'var(--iv-btn-fg)',
                      fontWeight: '600',
                      fontSize: '16px',
                      padding: '14px 24px',
                      minWidth: '150px',
                      cursor: (isProcessingResponseRef.current || (!isToolAppMode && isListeningRef.current)) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {currentQuestion >= totalQuestions ? '✓ End Question' : '✓ Next Question'}
                  </button>
                ) : null}
                
                <button className="btn btn-danger" onClick={endInterview}>
                  End Interview
                </button>
              </div>
            </div>

            <div className="transcript-container">
              <div className="transcript-toggle" onClick={() => setTranscriptVisible(!transcriptVisible)}>
                <span>{transcriptVisible ? 'Hide' : 'Show'} Transcript</span>
                <span>{transcriptVisible ? '▲' : '▼'}</span>
              </div>
              {transcriptVisible && (
                <div className="transcript-content">
                  {conversationHistory.length === 0 ? (
                    <div className="transcript-empty">Conversation will appear here...</div>
                  ) : (
                    conversationHistory.map((item, index) => (
                      <div key={index} className={`conversation-item ${item.speaker}`}>
                        <strong>{item.speaker === 'ai' ? 'AI Interviewer' : 'You'}:</strong> {item.message}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {currentView === 'feedback' && (
          <div className="feedback-section">
            <h2>Interview Analysis</h2>
            
            <div className="feedback-card">
              <h3>Overall Performance</h3>
              <div className="score-display">
                {feedback?.conversationAnalysis?.overall_score || 'Analyzing...'}
              </div>
              <div className="performance-analysis">
                {!conversationFeedbackReceived ? (
                  <div className="feedback-loading">Generating conversation analysis...</div>
                ) : (
                  <>
                    {feedback?.conversationAnalysis?.performance_level && (
                      <div className="feedback-item">
                        <strong>Performance Level:</strong>
                        <div dangerouslySetInnerHTML={{ __html: formatAsBulletPoints(feedback.conversationAnalysis.performance_level) }} />
                      </div>
                    )}
                    {feedback?.conversationAnalysis?.key_strengths && (
                      <div className="feedback-item">
                        <strong>Key Strengths:</strong>
                        <div dangerouslySetInnerHTML={{ __html: formatAsBulletPoints(feedback.conversationAnalysis.key_strengths) }} />
                      </div>
                    )}
                    {feedback?.conversationAnalysis?.areas_for_improvement && (
                      <div className="feedback-item">
                        <strong>Areas for Improvement:</strong>
                        <div dangerouslySetInnerHTML={{ __html: formatAsBulletPoints(feedback.conversationAnalysis.areas_for_improvement) }} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="feedback-card">
              <h3>Conversation Analysis</h3>
              <div className="conversation-analysis">
                {!conversationFeedbackReceived ? (
                  <div className="feedback-loading">Analyzing your responses...</div>
                ) : (
                  <>
                    {feedback?.conversationAnalysis?.technical_assessment && (
                      <div className="feedback-item">
                        <strong>Technical Assessment:</strong>
                        <div dangerouslySetInnerHTML={{ __html: formatAsBulletPoints(feedback.conversationAnalysis.technical_assessment) }} />
                      </div>
                    )}
                    {feedback?.conversationAnalysis?.communication_skills && (
                      <div className="feedback-item">
                        <strong>Communication Skills:</strong>
                        <div dangerouslySetInnerHTML={{ __html: formatAsBulletPoints(feedback.conversationAnalysis.communication_skills) }} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="feedback-card">
              <h3>Visual Presentation</h3>
              <div className="visual-analysis">
                {!visualFeedbackReceived ? (
                  <div className="feedback-loading">Processing visual presentation...</div>
                ) : (
                  <div className="feedback-item">
                    <div dangerouslySetInnerHTML={{ __html: formatAsBulletPoints(feedback?.visualPresentation || 'Visual analysis not available') }} />
                  </div>
                )}
              </div>
            </div>

            <div className="feedback-actions">
              {onComplete ? (
                <button 
                  className="btn btn-primary" 
                  onClick={() => {
                    console.log('Continue button clicked, calling onComplete');
                    if (onComplete && typeof onComplete === 'function') {
                      onComplete();
                    }
                  }}
                  disabled={!conversationFeedbackReceived}
                >
                  {conversationFeedbackReceived ? 'Continue to Next Step' : 'Loading Feedback...'}
                </button>
              ) : (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={restartInterview}
                    style={{ background: 'var(--iv-btn)', color: 'var(--iv-btn-fg)' }}
                  >
                    New Interview
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={downloadReport}
                    style={{ background: 'var(--iv-btn)', color: 'var(--iv-btn-fg)' }}
                  >
                    Download Report
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default WebsiteInterviewPage;

